import { NativeSelect, Table } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { Fragment, type ReactNode } from "react";
import type { ListParams } from "../lib/useListParams";
import { Button } from "./Button";
import { NARROW } from "./Drawer";
import { STRINGS } from "./strings";

/* One column model, two renderings.
 *
 * A screen writes its columns once, beside the select string that fetches
 * them. On a wide screen every column is a column. On a narrow screen a row
 * shows its leading fields on the first line, its secondary fields on a
 * second line in the muted color, and its detail fields only in the record.
 * Which fields lead is a design decision made in the screen's design pass,
 * and this component only carries it out. docs/design-standards.md, One
 * interface, every screen size, and docs/plans/front-end-overhaul.md, The
 * data table.
 *
 * Sorting and paging are the server's. A header click, or the sort control on
 * a narrow screen, writes ?sort and ?desc through ListParams exactly as the
 * headers did before, so a link to a sorted list works on either layout.
 *
 * The table owns no state of its own. The screen owns the query, the words
 * for its empty and error states (ListShell), and which columns are shown
 * (the column chooser, through `visible`). */
export type ColumnRole = "leading" | "secondary" | "detail";

export type ColumnSpec<Row> = {
  /** Stable, unique on the screen. */
  id: string;
  /** The heading on a wide screen and, unless narrowLabel says otherwise, the
   *  word before the value on a narrow row's second line. */
  header: string;
  role: ColumnRole;
  /** The view columns this column shows, so the column chooser can hide it.
   *  Empty means it is always shown, as an actions or status column is. */
  fields: readonly string[];
  cell: (row: Row) => ReactNode;
  /** Figures right-align in tabular figures, and a fresh sort on them starts
   *  with the biggest first. Text starts A to Z. */
  align?: "start" | "num";
  /** The server's sort key, when this column sorts. */
  sort?: string;
  /** A fresh sort on this column starts with the most first, as a count
   *  does, when the column is not right-aligned: a score, a date. */
  sortDescendingFirst?: boolean;
  /** On a narrow row, the word before the value. null when the value says
   *  what it is on its own, as a place or a name does. */
  narrowLabel?: string | null;
  /** The column that names the row. Set in the heading weight and color on
   *  both layouts, so a row has one place the eye lands first. */
  title?: boolean;
};

/** The visibility test the column chooser's choice gives a table. */
export function visibleWith<Row>(chosen: readonly string[]): (c: ColumnSpec<Row>) => boolean {
  return (c) => c.fields.length === 0 || c.fields.some((f) => chosen.includes(f));
}

type Props<Row> = {
  columns: readonly ColumnSpec<Row>[];
  rows: readonly Row[];
  rowKey: (row: Row) => string;
  params: ListParams;
  visible?: (c: ColumnSpec<Row>) => boolean;
  /** The open record's key. Its row is marked by background and weight, so it
   *  is told apart by more than color. */
  openId?: string | null | undefined;
  /** When given, a row opens a record on click and has a hover. A row that
   *  opens nothing has neither. */
  onOpen?: (row: Row) => void;
  /** The actions on a row, as a RowMenu. */
  rowMenu?: (row: Row) => ReactNode;
  /** Something shown under a row, such as the one-line form a row action opens. */
  rowExtra?: (row: Row) => ReactNode;
};

const cx = (...parts: (string | false | null | undefined)[]) => parts.filter(Boolean).join(" ");

/* A click on a link, a button or a control inside a row is that thing's
   click and not the row's. Without this, a click on a person's Employer on
   the People list opened the company and then the row opened the person on
   top of it, so the rep landed on the person with a stray company page in
   history. Found in the 2026-10-08 audit. */
function onTheRowItself(e: React.MouseEvent<HTMLElement>): boolean {
  const target = e.target as HTMLElement | null;
  return !target?.closest("a, button, input, select, textarea, label, [role='menu'], [role='menuitem']");
}

export function DataTable<Row>(props: Props<Row>) {
  const narrow = useMediaQuery(NARROW, false, { getInitialValueInEffect: false });
  const shown = props.columns.filter(props.visible ?? (() => true));
  return narrow ? <NarrowRows {...props} shown={shown} /> : <WideTable {...props} shown={shown} />;
}

/* A click on a column head. The same column again flips it. A different
   column becomes the primary sort and the one before stays as the tiebreak
   (James, 2026-10-08). A count starts with the biggest first and text starts
   A to Z, because nobody clicking a count wants the rows with none of it
   first, and nobody clicking a name wants Z. */
function sortOn<Row>(params: ListParams, c: ColumnSpec<Row>) {
  if (!c.sort) return;
  params.sortBy(c.sort, { numeric: c.align === "num" || Boolean(c.sortDescendingFirst) });
}

/** Where a column stands in the sort: 0 for primary, 1 and 2 for the
 *  tiebreaks, -1 when it is not sorted on. */
function sortRank<Row>(params: ListParams, c: ColumnSpec<Row>): number {
  return c.sort ? params.sorts.findIndex((s) => s.column === c.sort) : -1;
}

function WideTable<Row>({ shown, rows, rowKey, params, openId, onOpen, rowMenu, rowExtra }:
  Props<Row> & { shown: ColumnSpec<Row>[] }) {
  const span = shown.length + (rowMenu ? 1 : 0);
  // The column the order comes from gets a light tint down its length.
  const primary = params.sorts[0]?.column;
  return (
    <Table className="dt" stickyHeader>
      <Table.Thead>
        <Table.Tr>
          {shown.map((c) => {
            const rank = sortRank(params, c);
            const level = params.sorts[rank];
            const sortState = rank === 0 && level ? (level.ascending ? "ascending" : "descending") : "none";
            return c.sort ? (
              <Table.Th key={c.id}
                        className={cx("dt-th", c.align === "num" && "num", rank === 0 && "dt-sorted-primary", rank > 0 && "dt-sorted")}
                        aria-sort={sortState}>
                {/* The primary sort is the heavy head. A tiebreak keeps a
                    plain arrow, so the eye lands on the column that decides
                    the order and not on a rank it has to read. */}
                <button type="button" className="sort" onClick={() => sortOn(params, c)}
                        title={rank > 0 ? "A tiebreak after the primary sort" : rank === 0 ? "The primary sort" : "Sort on this column"}>
                  {c.header}
                  {level ? <span className="dt-sort-mark" aria-hidden="true">{level.ascending ? " ↑" : " ↓"}</span> : null}
                </button>
              </Table.Th>
            ) : (
              <Table.Th key={c.id} className={cx("dt-th", c.align === "num" && "num")}>{c.header}</Table.Th>
            );
          })}
          {rowMenu ? <Table.Th className="dt-th dt-menu-th">{STRINGS.actions}</Table.Th> : null}
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {rows.map((row) => {
          const key = rowKey(row);
          const extra = rowExtra?.(row);
          return (
            <Fragment key={key}>
              <Table.Tr
                className={cx("dt-tr", onOpen && "dt-opens", openId === key && "dt-open")}
                {...(onOpen ? { onClick: (e: React.MouseEvent<HTMLElement>) => { if (onTheRowItself(e)) onOpen(row); } } : {})}
              >
                {shown.map((c) => (
                  <Table.Td key={c.id}
                            className={cx("dt-td", c.align === "num" && "num", c.title && "dt-title", Boolean(c.sort) && c.sort === primary && "dt-col-sorted")}>
                    {c.cell(row)}
                  </Table.Td>
                ))}
                {rowMenu ? <Table.Td className="dt-td dt-menu-td">{rowMenu(row)}</Table.Td> : null}
              </Table.Tr>
              {extra ? (
                <Table.Tr className="dt-extra">
                  <Table.Td colSpan={span} className="dt-extra-td">{extra}</Table.Td>
                </Table.Tr>
              ) : null}
            </Fragment>
          );
        })}
      </Table.Tbody>
    </Table>
  );
}

function NarrowRows<Row>({ shown, rows, rowKey, params, openId, onOpen, rowMenu, rowExtra }:
  Props<Row> & { shown: ColumnSpec<Row>[] }) {
  const leading = shown.filter((c) => c.role === "leading");
  const secondary = shown.filter((c) => c.role === "secondary");
  const sortable = shown.filter((c) => c.sort);
  const current = sortable.find((c) => c.sort === params.sort);
  // A cell with no value is left out of a narrow row, so the row has no
  // stray separator or a label with nothing after it ("rank" with no rank).
  const filled = (v: ReactNode) => v !== null && v !== undefined && v !== "" && v !== false;
  return (
    <div className="dt-narrow">
      {sortable.length ? (
        <div className="dt-sort bar">
          <label htmlFor="dt-sort">{STRINGS.sortBy}</label>
          <NativeSelect
            id="dt-sort"
            value={current?.sort ?? params.sort}
            data={[
              ...(current ? [] : [{ value: params.sort, label: params.sort.replace(/_/g, " ") }]),
              ...sortable.map((c) => ({ value: c.sort as string, label: c.header })),
            ]}
            onChange={(e) => {
              const next = sortable.find((c) => c.sort === e.currentTarget.value);
              if (next) params.sortBy(next.sort as string, { numeric: next.align === "num" || Boolean(next.sortDescendingFirst) });
            }}
          />
          <Button
            size="compact-sm"
            aria-label={params.ascending ? STRINGS.sortAscending : STRINGS.sortDescending}
            title={params.ascending ? STRINGS.sortAscending : STRINGS.sortDescending}
            onClick={() => params.sortBy(params.sort, { ascending: !params.ascending })}
          >
            {params.ascending ? "↑" : "↓"}
          </Button>
        </div>
      ) : null}
      <ul className="dt-rows">
        {rows.map((row) => {
          const key = rowKey(row);
          const extra = rowExtra?.(row);
          return (
            <li key={key} className={cx("dt-row", onOpen && "dt-opens", openId === key && "dt-open")}>
              <div className="dt-row-line">
                <div className="dt-row-main" {...(onOpen ? { onClick: (e: React.MouseEvent<HTMLElement>) => { if (onTheRowItself(e)) onOpen(row); } } : {})}>
                  <div className="dt-lead">
                    {leading.map((c) => {
                      const v = c.cell(row);
                      return filled(v) ? <span key={c.id} className={cx("dt-lead-cell", c.title && "dt-title")}>{v}</span> : null;
                    })}
                  </div>
                  {secondary.length ? (
                    <div className="dt-sub">
                      {secondary.map((c) => {
                        const label = c.narrowLabel === undefined ? c.header : c.narrowLabel;
                        const v = c.cell(row);
                        if (!filled(v)) return null;
                        return (
                          <span key={c.id} className="dt-sub-cell">
                            {label ? <span className="dt-sub-label">{label} </span> : null}
                            {v}
                          </span>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
                {rowMenu ? <div className="dt-row-menu">{rowMenu(row)}</div> : null}
              </div>
              {extra ? <div className="dt-extra-narrow">{extra}</div> : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
